import { notification, Tag, Tooltip } from 'antd';
import { collection, getDocs } from 'firebase/firestore';
import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';

import ActionButton from '@src/components/Buttons/ActionButton';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumn } from '@src/components/Table';
import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type { IWorkJourneyManagementJourneyListItem } from '@src/models/interfaces/workJourneyManagement.interface';
import { ALLOWED_ROLES } from '@src/packages/utils';
import { getFullUrl } from '@src/packages/utils/functions';
import { getRouteToWorkJourneyMonthMatrix } from '@src/packages/workJourney/constants';
import type { WorkJourneyAssignedUser } from '@src/packages/workJourney/types';
import { useWorkJourneyValidationTranslations } from '@src/packages/workJourney/hooks/useWorkJourneyValidationTranslations';
import { notifyWorkJourneySaveError } from '@src/packages/workJourney/utils/submitWorkJourneyForm';
import WorkJourneyService from '@src/services/WorkJourneyService';

import { WORK_JOURNEY_JOURNEYS_TABLE_COLUMN_WIDTH } from '../../../../constants';
import { useWorkJourneyManagementContext } from '../../../../context';
import UsersSelectionDrawer from '../../../../../WorkJourneyCreate/components/UsersSelectionDrawer';
import { WorkJourneyMonthStatusBadge } from '../WorkJourneyMonthStatusBadge';
import styles from './styles.module.less';

interface WorkJourneyJourneysTableProps {
  items: IWorkJourneyManagementJourneyListItem[];
  loading?: boolean;
  month: number;
  year: number;
}

const workJourneyService = new WorkJourneyService();

const ROW_CELL_CLASS: Record<IWorkJourneyManagementJourneyListItem['rowHighlight'], string> = {
  critical: styles.cellCritical,
  warning: styles.cellWarning,
  normal: '',
};

const wrapCell = (record: IWorkJourneyManagementJourneyListItem, content: ReactNode) => {
  const markerClass = ROW_CELL_CLASS[record.rowHighlight];
  if (!markerClass) return content;

  return <div className={markerClass}>{content}</div>;
};

export const WorkJourneyJourneysTable = ({
  items,
  loading = false,
  month,
  year,
}: WorkJourneyJourneysTableProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { orgId, refreshJourneys } = useWorkJourneyManagementContext();
  const validationTranslations = useWorkJourneyValidationTranslations();

  const [isUsersDrawerVisible, setIsUsersDrawerVisible] = useState(false);
  const [selectedWorkJourneyId, setSelectedWorkJourneyId] = useState<string | null>(null);
  const [selectedDrawerUsers, setSelectedDrawerUsers] = useState<WorkJourneyAssignedUser[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);

  const dataSource = useMemo(
    () => items.map(item => ({ ...item, id: item.workJourneyConfig.id })),
    [items],
  );

  const openMatrix = (workJourneyConfigId: string) => {
    history.push(
      getFullUrl(getRouteToWorkJourneyMonthMatrix({ workJourneyConfigId, year, month }), url),
    );
  };

  const openMembersDrawer = async (record: IWorkJourneyManagementJourneyListItem) => {
    const workJourneyConfigId = record.workJourneyConfig.id;
    setSelectedWorkJourneyId(workJourneyConfigId);
    setIsUsersDrawerVisible(true);
    setDrawerLoading(true);

    try {
      const membersSnap = await getDocs(
        collection(
          db,
          FSCollections.ORGANIZATIONS,
          orgId,
          FSCollections.WORK_JOURNEY_CONFIGS,
          workJourneyConfigId,
          FSCollections.WORK_JOURNEY_MEMBERS,
        ),
      );

      const members = membersSnap.docs.map(docSnap => {
        const data = docSnap.data() as WorkJourneyAssignedUser;
        return {
          ...data,
          id: data.id || docSnap.id,
        };
      });

      setSelectedDrawerUsers(members);
    } catch (error) {
      notifyWorkJourneySaveError(error, intl, {
        messageId: 'workJourney.members.syncError',
        matchUrl: url,
      });
      setIsUsersDrawerVisible(false);
      setSelectedWorkJourneyId(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeMembersDrawer = () => {
    setIsUsersDrawerVisible(false);
    setSelectedWorkJourneyId(null);
    setSelectedDrawerUsers([]);
  };

  const handleMembersConfirm = async (selectedUsers: unknown[]) => {
    if (!selectedWorkJourneyId || !orgId) {
      closeMembersDrawer();
      return;
    }

    setSavingMembers(true);
    const members = selectedUsers as WorkJourneyAssignedUser[];
    try {
      await workJourneyService.updateMembers(
        selectedWorkJourneyId,
        orgId,
        members,
        validationTranslations,
      );
      notification.success({
        message: intl.formatMessage({ id: 'workJourney.members.syncSuccess' }),
      });
      closeMembersDrawer();
      await refreshJourneys();
    } catch (error) {
      notifyWorkJourneySaveError(error, intl, {
        messageId: 'workJourney.members.syncError',
        members,
        matchUrl: url,
      });
    } finally {
      setSavingMembers(false);
    }
  };

  const handleCoverageKeyDown = (
    event: KeyboardEvent<HTMLSpanElement>,
    record: IWorkJourneyManagementJourneyListItem,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void openMembersDrawer(record);
    }
  };

  const columns: ResponsiveTableColumn<IWorkJourneyManagementJourneyListItem>[] = [
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.journey' }),
      key: 'journey',
      render: record =>
        wrapCell(
          record,
          <Tooltip title={record.workJourneyConfig.name}>
            <span className={styles.journeyCell}>{record.workJourneyConfig.name}</span>
          </Tooltip>,
        ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.journeys.columns.shift' }),
      key: 'shift',
      width: WORK_JOURNEY_JOURNEYS_TABLE_COLUMN_WIDTH.SHIFT,
      render: record => wrapCell(record, record.shiftDescription),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.journeys.columns.monthStatus' }),
      key: 'monthStatus',
      width: WORK_JOURNEY_JOURNEYS_TABLE_COLUMN_WIDTH.MONTH_STATUS,
      render: record =>
        wrapCell(
          record,
          <div className={styles.statusCell}>
            {record.monthStatus.map((status, index) => (
              <WorkJourneyMonthStatusBadge
                key={`${status.type}-${index}`}
                type={status.type}
                dayCount={status.dayCount}
              />
            ))}
            {record.extraBadges?.map((badge, index) => (
              <Tag
                key={`${badge.type}-${index}`}
                color="purple"
                className={`gx-mb-0 gx-mr-0 ${styles.badge}`}
              >
                {badge.label}
              </Tag>
            ))}
          </div>,
        ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.journeys.columns.coverage' }),
      key: 'coverage',
      width: WORK_JOURNEY_JOURNEYS_TABLE_COLUMN_WIDTH.COVERAGE,
      align: 'center',
      render: record =>
        wrapCell(
          record,
          (() => {
            if (!record.coverage) {
              return (
                <span
                  role="button"
                  tabIndex={0}
                  className={`${styles.coverageCritical} ${styles.coverageClickable}`}
                  onClick={() => void openMembersDrawer(record)}
                  onKeyDown={event => handleCoverageKeyDown(event, record)}
                  aria-label={intl.formatMessage({ id: 'general.users.assigned' })}
                >
                  -
                </span>
              );
            }

            const isCritical = record.rowHighlight === 'critical';

            return (
              <span className={isCritical ? styles.coverageCritical : undefined}>
                {record.coverage.current}/{record.coverage.required}
              </span>
            );
          })(),
        ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      width: WORK_JOURNEY_JOURNEYS_TABLE_COLUMN_WIDTH.ACTIONS,
      columnType: 'actions',
      render: record =>
        wrapCell(
          record,
          <ActionButton.Container
            buttons={[
              <ActionButton
                key={`${record.workJourneyConfig.id}-matrix`}
                icon="info"
                tooltipText={intl.formatMessage({
                  id: 'workJourney.management.matrix.openMonthMatrix',
                })}
                allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
                onClick={() => openMatrix(record.workJourneyConfig.id)}
              />,
            ]}
          />,
        ),
    },
  ];

  return (
    <>
      <ResponsiveTable
        className={styles.journeysTable}
        dataSource={dataSource}
        columns={columns}
        loading={loading}
      />

      <UsersSelectionDrawer
        isVisible={isUsersDrawerVisible}
        onCancel={closeMembersDrawer}
        onConfirm={users => void handleMembersConfirm(users)}
        selectedUsers={selectedDrawerUsers}
        loading={drawerLoading || savingMembers}
        title={intl.formatMessage({ id: 'general.users.assigned' })}
      />
    </>
  );
};
